import { api } from "@/lib/api";
import type {
  ClientKind,
  CreateDownloadClient,
  DownloadClientRow,
  DownloadStatus,
  EmbeddedTestResult,
  TestResult,
  UpdateDownloadClient,
} from "./types";

/// Discriminated union returned by `test(id)`. `kind` mirrors the
/// row's `ClientKind`: `embedded` rows return a multi-server breakdown,
/// every other kind returns the simple `TestResult`.
export type AnyTestResult =
  | { kind: "embedded"; result: EmbeddedTestResult }
  | { kind: Exclude<ClientKind, "embedded">; result: TestResult };

export const downloadClientsApi = {
  list: () => api<DownloadClientRow[]>("/api/v1/download-clients"),
  get: (id: number) => api<DownloadClientRow>(`/api/v1/download-clients/${id}`),
  create: (body: CreateDownloadClient) =>
    api<DownloadClientRow>("/api/v1/download-clients", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  update: (id: number, body: UpdateDownloadClient) =>
    api<DownloadClientRow>(`/api/v1/download-clients/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  remove: (id: number) =>
    api<void>(`/api/v1/download-clients/${id}`, { method: "DELETE" }),
  /**
   * Test a saved client. The backend returns a different shape for
   * `kind=embedded` (multi-server breakdown) so we dispatch on the row's
   * kind and return a discriminated union the caller can switch on.
   */
  test: async (row: DownloadClientRow): Promise<AnyTestResult> => {
    if (row.kind === "embedded") {
      const result = await api<EmbeddedTestResult>(
        `/api/v1/download-clients/${row.id}/test`,
        { method: "POST" },
      );
      return { kind: "embedded", result };
    }
    const result = await api<TestResult>(`/api/v1/download-clients/${row.id}/test`, {
      method: "POST",
    });
    return { kind: row.kind, result };
  },
  /**
   * Test an unsaved configuration without persisting. Used by the
   * Add / Edit modal so users can validate before saving. The embedded
   * row never reaches this path — it's auto-seeded.
   */
  testConfig: (body: CreateDownloadClient) =>
    api<TestResult>("/api/v1/download-clients/test", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  queue: () => api<DownloadStatus[]>("/api/v1/queue"),
};
